import React from "react";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import PauseCircleOutlineOutlinedIcon from "@mui/icons-material/PauseCircleOutlineOutlined";
import PlayCircleOutlineOutlinedIcon from "@mui/icons-material/PlayCircleOutlineOutlined";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import PaymentsOutlinedIcon from "@mui/icons-material/PaymentsOutlined";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import StatusPill from "./StatusPill";
import {
  PLAN_TYPES,
  formatDate,
  price,
  labelFrom,
  formatVariantSize,
  formatMeasure,
  formatSubscriptionFrequency,
  formatSubscriptionPayment,
  formatDeliverySlot,
} from "./subscriptionUtils";

const SubscriptionCard = ({
  subscription,
  onDetail,
  onPause,
  onResume,
  onEdit,
  onCancel,
  onSetupAutopay,
  actionLoading,
}) => {
  const status = String(subscription.status || "").toLowerCase();
  const canPause = status === "active";
  const canResume = status === "paused";
  const canEdit = ["active", "paused"].includes(status);
  const canCancel = !["completed", "cancelled"].includes(status);
  const canSetupAutopay =
    status === "pending_mandate" &&
    String(subscription.payment_method || "").toLowerCase() === "upi_autopay";
  const twiceDaily = String(subscription.frequency || "").toLowerCase() === "twice_a_day";
  const variantSize = formatVariantSize(subscription);
  const primarySlot = subscription.morning_slot || subscription.delivery_slot;
  const recipientName =
    subscription.recipient_name ||
    subscription.customer_name ||
    subscription.name ||
    subscription.address?.name ||
    "";
  const mobileNumber =
    subscription.mobile ||
    subscription.phone ||
    subscription.customer_mobile ||
    subscription.customer_phone ||
    subscription.address?.mobile ||
    subscription.address?.phone ||
    "";
  const addressText =
    typeof subscription.address === "string"
      ? subscription.address
      : [
          subscription.address?.address || subscription.delivery_address || subscription.street,
          subscription.address?.city || subscription.city,
          subscription.address?.state || subscription.state,
          subscription.address?.pincode || subscription.pincode,
        ]
          .filter(Boolean)
          .join(", ");

  return (
    <div className="overflow-hidden rounded-3xl border border-gray-100 bg-white p-3 shadow-md shadow-black/5 transition hover:shadow-lg sm:rounded-2xl sm:p-5">
      <div className="flex gap-3 sm:gap-4">
        <div className="flex h-16 w-16 shrink-0 items-center justify-center overflow-hidden rounded-2xl bg-gray-50 sm:h-20 sm:w-20">
          {subscription.image1 ? (
            <img
              src={subscription.image1}
              alt={subscription.product_name}
              className="h-full w-full object-contain"
            />
          ) : (
            <Inventory2OutlinedIcon className="text-gray-300" />
          )}
        </div>

        <div className="min-w-0 flex-1">
          <div className="mb-3 flex flex-wrap items-start justify-between gap-2 sm:mb-2">
            <div className="min-w-0">
              <h3 className="line-clamp-2 text-sm font-extrabold leading-snug text-gray-900 sm:text-base">
                {subscription.product_name || "Milk Subscription"}
              </h3>
              <p className="mt-0.5 text-[10px] font-semibold text-gray-400">Subscription #{subscription.subscription_id}</p>
              {variantSize && (
                <span className="mt-1 inline-flex items-center gap-1 rounded-lg bg-primary-50 px-2 py-0.5 text-[11px] font-bold text-primary-700 sm:px-2.5 sm:text-xs">
                  <span className="text-primary-400">Size:</span>{" "}
                  {variantSize}
                </span>
              )}
            </div>
            <StatusPill status={subscription.status} />
          </div>

          <div className="grid grid-cols-2 gap-2 text-[11px] sm:grid-cols-4 sm:text-xs">
            <div className="rounded-xl bg-gray-50 px-2.5 py-2 sm:px-3">
              <p className="font-bold uppercase tracking-wide text-gray-400 sm:normal-case sm:tracking-normal">{twiceDaily ? "Daily total" : "Per delivery"}</p>
              <p className="font-extrabold text-gray-800">
                {formatMeasure(subscription.quantity, subscription.quantity_unit || subscription.unit_name)}
              </p>
            </div>
            <div className="rounded-xl bg-gray-50 px-2.5 py-2 sm:px-3">
              <p className="font-bold uppercase tracking-wide text-gray-400 sm:normal-case sm:tracking-normal">Frequency · Plan</p>
              <p className="font-extrabold text-gray-800">
                {formatSubscriptionFrequency(subscription.frequency)} · {labelFrom(PLAN_TYPES, subscription.plan_type)}
              </p>
            </div>
            <div className="rounded-xl bg-gray-50 px-2.5 py-2 sm:px-3">
              <p className="font-bold uppercase tracking-wide text-gray-400 sm:normal-case sm:tracking-normal">Next delivery</p>
              <p className="font-extrabold text-gray-800">
                {status === "cancelled" ? "—" : subscription.next_delivery ? formatDate(subscription.next_delivery) : status === "pending_mandate" ? "Awaiting AutoPay" : "Not scheduled"}
              </p>
            </div>
            <div className="rounded-xl bg-gray-50 px-2.5 py-2 sm:px-3">
              <p className="font-bold uppercase tracking-wide text-gray-400 sm:normal-case sm:tracking-normal">Plan amount</p>
              <p className="font-extrabold text-gray-800">
                {price(subscription.total_plan_amount)}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="mt-4 rounded-2xl border border-primary-100 bg-gradient-to-r from-primary-50/80 to-emerald-50/70 p-3 sm:p-4">
        <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
          <p className="text-[11px] font-extrabold uppercase tracking-wide text-primary-800">Delivery schedule</p>
          <span className="rounded-full bg-white/80 px-2.5 py-1 text-[10px] font-bold text-gray-600">{formatSubscriptionPayment(subscription.payment_method)}</span>
        </div>
        {twiceDaily ? (
          <div className="grid gap-2 sm:grid-cols-2">
            <div className="rounded-xl bg-white/80 px-3 py-2">
              <p className="text-[10px] font-bold uppercase tracking-wide text-gray-400">Morning</p>
              <p className="mt-0.5 text-xs font-extrabold text-gray-800">{formatMeasure(subscription.morning_quantity, subscription.quantity_unit || subscription.unit_name)} <span className="font-semibold text-gray-500">· {formatDeliverySlot(subscription.morning_slot)}</span></p>
            </div>
            <div className="rounded-xl bg-white/80 px-3 py-2">
              <p className="text-[10px] font-bold uppercase tracking-wide text-gray-400">Evening</p>
              <p className="mt-0.5 text-xs font-extrabold text-gray-800">{formatMeasure(subscription.evening_quantity, subscription.quantity_unit || subscription.unit_name)} <span className="font-semibold text-gray-500">· {formatDeliverySlot(subscription.evening_slot)}</span></p>
            </div>
          </div>
        ) : (
          <p className="text-xs font-extrabold text-gray-800">{formatDeliverySlot(primarySlot)} <span className="font-semibold text-gray-500">· {formatMeasure(subscription.quantity, subscription.quantity_unit || subscription.unit_name)} per delivery</span></p>
        )}
        <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 border-t border-primary-100 pt-2 text-[10px] font-semibold text-gray-500">
          <span>Unit price: {price(subscription.unit_price)}</span>
          <span>Starts: {formatDate(subscription.start_date)}</span>
          <span>Ends: {formatDate(subscription.end_date)}</span>
        </div>
      </div>

      {(addressText || recipientName || mobileNumber) && (
        <div className="mt-3 flex items-start gap-2.5 rounded-2xl border border-gray-100 bg-gray-50/80 px-3.5 py-2.5 text-xs text-gray-600">
          <LocationOnOutlinedIcon className="mt-0.5 shrink-0 text-[#046a38]" style={{ fontSize: 16 }} />
          <div className="min-w-0 flex-1">
            {(recipientName || mobileNumber) && (
              <div className="flex flex-wrap items-center gap-x-2 text-xs font-bold text-gray-900">
                {recipientName && <span>{recipientName}</span>}
                {mobileNumber && (
                  <span className="font-semibold text-gray-600 flex items-center gap-1">
                    • <a href={`tel:${mobileNumber}`} className="hover:text-[#046a38] hover:underline">{mobileNumber}</a>
                  </span>
                )}
              </div>
            )}
            {addressText && (
              <p className="mt-0.5 line-clamp-1 text-[11px] text-gray-500">
                {addressText}
              </p>
            )}
          </div>
        </div>
      )}

      {subscription.active_pause && (
        <div className="mt-4 rounded-xl bg-amber-50 px-4 py-3 text-sm text-amber-800">
          <p className="font-bold">
            Paused from {formatDate(subscription.active_pause.pause_from)} to{" "}
            {formatDate(subscription.active_pause.pause_to)}
          </p>
          {subscription.active_pause.reason && (
            <p className="text-xs font-semibold">
              {String(subscription.active_pause.reason).replace(/_/g, " ")}
            </p>
          )}
        </div>
      )}

      <div className="mt-3 grid grid-cols-2 gap-2 border-t border-gray-100 pt-3 sm:mt-4 sm:flex sm:flex-wrap sm:border-0 sm:pt-0">
        <button
          type="button"
          onClick={() => onDetail(subscription)}
          className="flex items-center justify-center gap-1.5 rounded-xl border border-gray-200 px-3 py-2 text-xs font-bold text-gray-600 transition hover:bg-gray-50"
        >
          <VisibilityOutlinedIcon style={{ fontSize: 15 }} /> Details
        </button>
        {canEdit && (
          <button
            type="button"
            onClick={() => onEdit(subscription)}
            className="flex items-center justify-center gap-1.5 rounded-xl border border-gray-200 px-3 py-2 text-xs font-bold text-gray-600 transition hover:bg-gray-50"
          >
            <EditOutlinedIcon style={{ fontSize: 15 }} /> Edit
          </button>
        )}
        {canPause && (
          <button
            type="button"
            onClick={() => onPause(subscription)}
            className="flex items-center justify-center gap-1.5 rounded-xl bg-amber-50 px-3 py-2 text-xs font-bold text-amber-700 transition hover:bg-amber-100"
          >
            <PauseCircleOutlineOutlinedIcon style={{ fontSize: 15 }} /> Pause
          </button>
        )}
        {canResume && (
          <button
            type="button"
            onClick={() => onResume(subscription)}
            className="flex items-center justify-center gap-1.5 rounded-xl bg-primary-50 px-3 py-2 text-xs font-bold text-primary-700 transition hover:bg-primary-100"
          >
            <PlayCircleOutlineOutlinedIcon style={{ fontSize: 15 }} /> Resume
          </button>
        )}
        {canSetupAutopay && (
          <button
            type="button"
            disabled={actionLoading}
            onClick={() => onSetupAutopay(subscription)}
            className="flex items-center justify-center gap-1.5 rounded-xl bg-emerald-50 px-3 py-2 text-xs font-bold text-emerald-700 transition hover:bg-emerald-100 disabled:opacity-50"
          >
            <PaymentsOutlinedIcon style={{ fontSize: 15 }} /> Setup AutoPay
          </button>
        )}
        {canCancel && (
          <button
            type="button"
            disabled={actionLoading}
            onClick={() => onCancel(subscription)}
            className="flex items-center justify-center gap-1.5 rounded-xl bg-red-50 px-3 py-2 text-xs font-bold text-red-600 transition hover:bg-red-100 disabled:opacity-50 sm:ml-auto"
          >
            <DeleteOutlineOutlinedIcon style={{ fontSize: 15 }} /> Cancel
          </button>
        )}
      </div>
    </div>
  );
};

export default SubscriptionCard;
